import { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { getAll, remove, flushQueue } from "@/lib/offlineQueue";
import { preloadOfflineData, getPreloadInfo } from "@/lib/offlinePreload";
import { PageHeader } from "@/components/pcp";
import { toast } from "sonner";
import { TrashSimple, CloudArrowUp, WifiSlash, DownloadSimple, CheckCircle } from "@phosphor-icons/react";

const fmtDataHora = (ts) => {
  const dt = new Date(ts);
  return `${String(dt.getDate()).padStart(2, "0")}/${String(dt.getMonth() + 1).padStart(2, "0")}/${dt.getFullYear()} ${String(dt.getHours()).padStart(2, "0")}:${String(dt.getMinutes()).padStart(2, "0")}`;
};

function PrepararOffline({ online }) {
  const [info, setInfo] = useState(getPreloadInfo());
  const [baixando, setBaixando] = useState(false);
  useEffect(() => {
    const upd = () => setInfo(getPreloadInfo());
    window.addEventListener("offline-preload-changed", upd);
    return () => window.removeEventListener("offline-preload-changed", upd);
  }, []);
  const baixar = async () => {
    setBaixando(true);
    try {
      const r = await preloadOfflineData();
      toast.success(`Pronto para offline: ${r.ordens} OS e ${r.itens} itens salvos no aparelho`);
    } catch {
      toast.error("Falha ao baixar dados — verifique a conexão");
    } finally {
      setBaixando(false);
    }
  };
  return (
    <div className="pcp-card" style={{ padding: 16, marginBottom: 16, display: "flex", gap: 14, alignItems: "center", flexWrap: "wrap" }} data-testid="preparar-offline-card">
      <div style={{ flex: 1, minWidth: 220 }}>
        <div className="font-display font-bold" style={{ fontSize: "1rem", display: "flex", alignItems: "center", gap: 8 }}>
          <DownloadSimple size={18} weight="bold" /> Dados para trabalhar offline
        </div>
        <div style={{ fontSize: "0.8rem", color: "#71717A", marginTop: 4 }} data-testid="preparar-offline-status">
          {info ? (
            <><CheckCircle size={14} weight="bold" style={{ verticalAlign: -2, color: "#16A34A" }} /> Atualizado em <strong>{fmtDataHora(info.at)}</strong> · {info.ordens} OS ativas · {info.itens} itens</>
          ) : "Nenhum dado baixado ainda. Baixe antes de ir para o campo."}
        </div>
        <div style={{ fontSize: "0.72rem", color: "#A1A1AA", marginTop: 4 }}>Baixa OS ativas, itens, trechos, equipes e composições. Também roda sozinho ao abrir o app com internet.</div>
      </div>
      <button className="btn btn-primary" onClick={baixar} disabled={!online || baixando} data-testid="btn-preparar-offline">
        <DownloadSimple size={16} weight="bold" /> {baixando ? "Baixando…" : "Baixar dados agora"}
      </button>
    </div>
  );
}

export default function FilaOffline() {
  const [items, setItems] = useState([]);
  const [syncing, setSyncing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [online, setOnline] = useState(navigator.onLine);

  const reload = useCallback(async () => {
    setLoading(true);
    const list = await getAll();
    // Ordena por data mais recente primeiro
    setItems([...list].sort((a, b) => (b.created_at || 0) - (a.created_at || 0)));
    setLoading(false);
  }, []);

  useEffect(() => {
    reload();
    const onOnline = () => setOnline(true);
    const onOffline = () => setOnline(false);
    window.addEventListener("online", onOnline);
    window.addEventListener("offline", onOffline);
    window.addEventListener("offline-queue-changed", reload);
    return () => {
      window.removeEventListener("online", onOnline);
      window.removeEventListener("offline", onOffline);
      window.removeEventListener("offline-queue-changed", reload);
    };
  }, [reload]);

  const excluirItem = async (id) => {
    if (!window.confirm("Excluir este apontamento pendente? Ele NÃO será enviado ao servidor.")) return;
    await remove(id);
    toast.success("Apontamento removido da fila");
  };

  const enviarTodos = async () => {
    if (!navigator.onLine) {
      toast.error("Sem conexão com a internet");
      return;
    }
    setSyncing(true);
    const { ok, fail, skipped } = await flushQueue((ep, payload) => api.post(ep, payload));
    setSyncing(false);
    await reload();
    if (skipped) { toast.info("Envio já em andamento"); return; }
    if (ok > 0) toast.success(`${ok} apontamento${ok > 1 ? "s" : ""} enviado${ok > 1 ? "s" : ""}`);
    if (fail > 0) toast.error(`${fail} falha${fail > 1 ? "s" : ""} — tente de novo mais tarde`);
  };

  const enviarUm = async (item) => {
    if (!navigator.onLine) {
      toast.error("Sem conexão com a internet");
      return;
    }
    const { ok, skipped } = await flushQueue((ep, payload) => api.post(ep, payload), [item.id]);
    if (skipped) toast.info("Envio já em andamento");
    else if (ok) toast.success("Apontamento enviado");
    else toast.error("Falha ao enviar — verifique a conexão");
  };

  return (
    <>
      <PageHeader
        testid="page-fila-offline"
        title="Fila Offline"
        subtitle={`${items.length} apontamento${items.length !== 1 ? "s" : ""} pendente${items.length !== 1 ? "s" : ""} de envio`}
        actions={
          items.length > 0 && (
            <button
              className="btn btn-primary btn-lg"
              onClick={enviarTodos}
              disabled={syncing || !online}
              data-testid="btn-enviar-todos"
            >
              <CloudArrowUp size={18} weight="bold" /> {syncing ? "Enviando…" : "Enviar todos"}
            </button>
          )
        }
      />
      <div className="page-body">
        <PrepararOffline online={online} />
        {!online && (
          <div style={{ padding: 14, background: "#FEE2E2", border: "1px solid #DC2626", borderRadius: 4, marginBottom: 16, color: "#991B1B", display: "flex", alignItems: "center", gap: 10 }}>
            <WifiSlash size={20} weight="bold" />
            <div style={{ fontSize: "0.9rem" }}>Você está <strong>sem internet</strong>. Os apontamentos ficarão aqui até voltar o sinal.</div>
          </div>
        )}

        {loading ? (
          <div style={{ padding: 40, textAlign: "center", color: "#71717A" }}>Carregando fila…</div>
        ) : items.length === 0 ? (
          <div style={{ padding: 60, textAlign: "center" }}>
            <div style={{ fontSize: "3rem", opacity: 0.3 }}>✓</div>
            <div style={{ marginTop: 12, color: "#71717A" }}>Nenhum apontamento pendente. Fila vazia.</div>
          </div>
        ) : (
          <div style={{ display: "grid", gap: 12 }}>
            {items.map((it) => {
              const p = it.payload?.pintura || {};
              const pl = it.payload?.placa;
              const foto0 = it.payload?.fotos?.[0];
              const dt = new Date(it.created_at || Date.now());
              const dtLabel = fmtDataHora(dt);
              const area = pl ? (pl.area_total_m2 || it.payload?.quantidade || 0) : (p.area_m2_cliente || it.payload?.quantidade || 0);
              const sp = it.payload?.suporte_de;
              const titulo = pl ? `Placa ${pl.codigo}${pl.nome ? " · " + pl.nome : ""}` : sp ? `Poste/suporte da placa ${sp.codigo} · ${it.payload?.quantidade} un` : (p.tipo_label || p.tipo || "Apontamento de produção");
              const sub = pl
                ? `km ${pl.km || "?"} · ${pl.lado_via || ""}${pl.qtd_placas ? ` · ${pl.qtd_placas} un` : ""}`
                : `${p.km_inicial ? `km ${p.km_inicial} → ${p.km_final || "?"} · ` : ""}${p.cor || ""}${p.largura_cm ? ` · ${p.largura_cm}cm` : ""}`;
              return (
                <div key={it.id} data-testid={`fila-item-${it.id}`} style={{ display: "flex", gap: 12, padding: 14, background: "#FFF", border: "1px solid #E4E4E7", borderRadius: 4 }}>
                  {foto0 ? (
                    <img src={foto0} alt="" style={{ width: 78, height: 78, objectFit: "cover", borderRadius: 3, flexShrink: 0 }} />
                  ) : (
                    <div style={{ width: 78, height: 78, background: "#FAFAFA", borderRadius: 3, display: "flex", alignItems: "center", justifyContent: "center", color: "#A1A1AA", fontSize: "0.7rem", flexShrink: 0 }}>Sem foto</div>
                  )}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 8 }}>
                      <div>
                        <div style={{ fontWeight: 700, fontSize: "0.95rem" }}>
                          {titulo}
                          {p.metodo && <span style={{ marginLeft: 8, padding: "2px 6px", background: "#0A0A0A", color: "#FFCC00", fontSize: "0.65rem", borderRadius: 3, textTransform: "uppercase", letterSpacing: "0.05em" }}>{p.metodo}</span>}
                        </div>
                        <div style={{ fontSize: "0.78rem", color: "#71717A", marginTop: 2 }}>{sub}</div>
                        <div style={{ fontSize: "0.72rem", color: "#71717A", marginTop: 2 }}>Registrado em {dtLabel}</div>
                        {it.last_error && (
                          <div style={{ fontSize: "0.72rem", color: "#DC2626", marginTop: 4 }}>⚠️ Última tentativa: {it.last_error}</div>
                        )}
                      </div>
                      <div className="font-mono font-semibold" style={{ fontSize: "1.1rem", whiteSpace: "nowrap" }}>
                        {area.toLocaleString("pt-BR", { maximumFractionDigits: 2 })} <span style={{ fontSize: "0.7rem", color: "#71717A" }}>{pl && !pl.area_total_m2 ? "un" : "m²"}</span>
                      </div>
                    </div>
                    <div style={{ display: "flex", gap: 6, marginTop: 8 }}>
                      <button
                        className="btn btn-outline btn-sm"
                        onClick={() => enviarUm(it)}
                        disabled={!online || syncing}
                        data-testid={`btn-enviar-${it.id}`}
                      >Enviar</button>
                      <button
                        className="btn btn-outline btn-sm"
                        onClick={() => excluirItem(it.id)}
                        style={{ borderColor: "#DC2626", color: "#DC2626" }}
                        data-testid={`btn-excluir-${it.id}`}
                      ><TrashSimple size={14} weight="bold" /> Excluir</button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </>
  );
}
